import { IngredientKind } from '@sg/graphql-schema';

import { type Guard, type Modification, type Product } from '../../types';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * A guard to verify if a product with potential dressing options does not
 * have any dressing modifications selected.
 */
export const checkIfNoDressingSelected: Guard = function (args) {
  const { context } = args;

  const { product, modifications } = context;

  // ─── Missing Data ────────────────────────────────────────────────────

  const hasMissingData = !modifications || !product?.ingredients;

  if (hasMissingData) {
    return false;
  }

  // ─────────────────────────────────────────────────────────────────────

  const { active } = modifications;
  const { isCustom, ingredients } = product;

  const productHasDressing = ingredients.some(checkIfDressingIngredient);
  const hasSelectedDressing = active.some(checkIfDressingModification);

  const canHaveDressing = productHasDressing || isCustom;

  return canHaveDressing && !hasSelectedDressing;
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function checkIfDressingIngredient(ingredient: Ingredient) {
  return ingredient.kind === IngredientKind.Dressing;
}

function checkIfDressingModification(modification: Modification) {
  return modification.ingredient?.kind === IngredientKind.Dressing;
}

// ─── Types ───────────────────────────────────────────────────────────────────

type Ingredient = Product['ingredients'][number];
